import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, EmbeddedActionBar } from "../components/ui-bits";
import { Plus, Trash2, Edit3, Search } from "lucide-react";
import { useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

const empty = { name: "", code: "", composition: "", color: "", unit: "m", stock: 0, cost_per_unit: 0, supplier_id: "", notes: "" };
const UNITS = ["m", "kg", "unit", "roll"];

export default function MaterialsPage({ embedded = false } = {}) {
  const { canEdit } = useBrandPermissions();
  const t = useT();
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/materials", { params: { search: search || undefined } }).then(r => setList(r.data));
  useEffect(() => { load(); api.get("/suppliers").then(r=>setSuppliers(r.data)).catch(()=>{}); }, [search]);

  const open = (row) => { setError(""); if (row) { setForm({ ...empty, ...row }); setEditId(row.id); } else { setForm(empty); setEditId(null); } setModal(true); };
  useAutoOpenFromQuery(list, open);
  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = { ...form, stock: Number(form.stock)||0, cost_per_unit: Number(form.cost_per_unit)||0, min_stock: Number(form.min_stock)||0 };
      if (editId) await api.put(`/materials/${editId}`, payload);
      else await api.post("/materials", payload);
      setModal(false); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/materials/${id}`); load(); };

  return (
    <div data-testid="materials-page">
      {!embedded && (
      <PageHeader eyebrow="Fabrics & Trims" title="Materials"
        action={<button data-testid="new-material-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2"><Plus size={14}/>New Material</button>}/>
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <button data-testid="new-material-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
            <Plus size={14}/> {t("materials.new")}
          </button>
        }/>
      )}

      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
        <input data-testid="materials-search" placeholder={t("common.search")} value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
      </div>
      {list.length === 0 ? (
        <Empty
          title={t("materials.empty")}
          cta={canEdit ? { label: t("materials.cta_first"), onClick: ()=>open(null) } : null}
          ctaTestId="materials-empty-cta"
        />
      ) : (
        <div className="card-dark">
          <table className="tbl" data-testid="materials-table">
            <thead><tr><th>Code</th><th>Name</th><th>Composition</th><th>Color</th><th>Stock</th><th>Cost/Unit</th><th></th></tr></thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id}>
                  <td className="text-[#0A0A0A]">{m.code || "—"}</td>
                  <td>{m.name}</td>
                  <td className="text-[#525252]">{m.composition || "—"}</td>
                  <td>{m.color || "—"}</td>
                  <td>{m.stock} {m.unit}{m.min_stock > 0 && <span className="text-xs opacity-60" data-testid={`material-min-${m.id}`}> · min {m.min_stock}</span>}</td>
                  <td>€ {m.cost_per_unit?.toFixed(2)}</td>
                  <td className="text-right">
                    <button data-testid={`material-edit-${m.id}`} onClick={()=>open(m)} className="text-[#525252] hover:text-[#0A0A0A] mr-3"><Edit3 size={14}/></button>
                    <button data-testid={`material-delete-${m.id}`} onClick={()=>remove(m.id)} className="text-[#525252] hover:text-red-600"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={modal} onClose={()=>setModal(false)} title={editId ? "Edit material" : "New material"} wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[["name","Name",true],["code","Code"],["composition","Composition"],["color","Color"]].map(([k,l,r])=>(
            <div key={k}><label className="label-caps block mb-2">{l}</label>
              <input data-testid={`material-form-${k}`} required={r} className="input-dark" value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})}/></div>
          ))}
          <div><label className="label-caps block mb-2">Unit</label>
            <select className="input-dark" value={form.unit} onChange={(e)=>setForm({...form,unit:e.target.value})}>{UNITS.map(u=><option key={u}>{u}</option>)}</select></div>
          <div><label className="label-caps block mb-2">Stock</label><input type="number" step="0.01" className="input-dark" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Cost per unit €</label><input type="number" step="0.01" className="input-dark" value={form.cost_per_unit} onChange={(e)=>setForm({...form,cost_per_unit:e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Scorta minima ({form.unit})</label><input data-testid="material-form-min_stock" type="number" step="0.01" min="0" className="input-dark" value={form.min_stock ?? 0} onChange={(e)=>setForm({...form,min_stock:e.target.value})}/>
            <div className="text-xs opacity-60 mt-1">0 = nessun avviso. Controllata sulla quantità libera (magazzino meno riservato).</div></div>
          <div><label className="label-caps block mb-2">Supplier</label>
            <select className="input-dark" value={form.supplier_id||""} onChange={(e)=>setForm({...form,supplier_id:e.target.value})}>
              <option value="">—</option>{suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div className="md:col-span-2"><label className="label-caps block mb-2">Notes</label>
            <textarea rows={2} className="input-dark" value={form.notes||""} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
          {error && <div className="text-red-600 text-sm md:col-span-2">{error}</div>}
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">Cancel</button>
            <button data-testid="material-form-save" className="btn-gold">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
